import { useState, useEffect, useRef } from 'react'
import { listUsers, listUserCreditRecords, grantCredits, type AdminUser, type AdminCreditRecord } from '@/services/admin_users'
import { useAdminAuth } from '../components/AdminAuthProvider'

export function UsersAdminView() {
  const { connected, connecting } = useAdminAuth()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // 选中的用户
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [creditRecords, setCreditRecords] = useState<AdminCreditRecord[]>([])
  const [recordsLoading, setRecordsLoading] = useState(false)
  const [recordsPage, setRecordsPage] = useState(1)
  const [recordsTotalPages, setRecordsTotalPages] = useState(1)

  // 充值表单
  const [grantAmount, setGrantAmount] = useState('')
  const [grantReason, setGrantReason] = useState('')
  const [granting, setGranting] = useState(false)

  // Use refs to store latest auth state to avoid dependency issues
  const connectedRef = useRef(connected)
  const connectingRef = useRef(connecting)
  connectedRef.current = connected
  connectingRef.current = connecting

  // Fetch users - defined outside useCallback to avoid dependency issues
  const fetchUsers = async (searchQuery: string, pageNum: number, signal?: AbortSignal) => {
    // Don't fetch if not connected or still connecting
    if (!connectedRef.current || connectingRef.current) {
      return
    }
    setLoading(true)
    try {
      const res = await listUsers(
        { q: searchQuery || undefined, page: pageNum, page_size: 20 },
        { signal }
      )
      setUsers(res.items)
      setTotalPages(res.total_pages)
    } catch (err) {
      if ((err as any)?.name === 'AbortError') {
        return
      }
      console.error('Failed to fetch users:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch credit records
  const fetchCreditRecords = async (userId: string, p: number, signal?: AbortSignal) => {
    // Don't fetch if not connected
    if (!connectedRef.current || connectingRef.current) {
      return
    }
    setRecordsLoading(true)
    try {
      const res = await listUserCreditRecords(
        userId,
        { page: p, page_size: 20 },
        { signal }
      )
      setCreditRecords(res.items)
      setRecordsTotalPages(res.total_pages)
    } catch (err) {
      if ((err as any)?.name === 'AbortError') {
        return
      }
      console.error('Failed to fetch credit records:', err)
    } finally {
      setRecordsLoading(false)
    }
  }

  // Fetch users when auth is ready or query params change
  useEffect(() => {
    if (!connected || connecting) {
      return
    }
    const controller = new AbortController()
    fetchUsers(search, page, controller.signal)
    return () => {
      controller.abort()
    }
  }, [connected, connecting, search, page])

  // Fetch credit records when selectedUser or recordsPage changes
  useEffect(() => {
    if (!selectedUser || !connected || connecting) {
      return
    }
    const controller = new AbortController()
    fetchCreditRecords(selectedUser.user_id, recordsPage, controller.signal)
    return () => {
      controller.abort()
    }
  }, [selectedUser, recordsPage, connected, connecting])

  const handleSelectUser = (user: AdminUser) => {
    setSelectedUser(user)
    setRecordsPage(1)
    setCreditRecords([])
  }

  const handleGrant = async () => {
    if (!selectedUser || !grantAmount || !grantReason) return
    setGranting(true)
    try {
      await grantCredits(selectedUser.user_id, parseFloat(grantAmount), grantReason)
      setGrantAmount('')
      setGrantReason('')
      fetchUsers(search, page)
      fetchCreditRecords(selectedUser.user_id, recordsPage)
      alert('Credits granted successfully')
    } catch (err: any) {
      alert(err.message || 'Failed to grant credits')
    } finally {
      setGranting(false)
    }
  }

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>User Management</h1>
          <p className="admin-page-subtitle">Manage user accounts and credit balances</p>
        </div>
      </div>

      {/* 工具栏 */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-search">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by username or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
        </div>
        <div className="admin-toolbar-stats">
          <span className="stat-item">
            <span className="stat-value">{users.length}</span>
            <span className="stat-label">Users</span>
          </span>
        </div>
      </div>

      <div className="admin-page-content">
        {/* 用户列表 */}
        <div className="admin-card admin-card--table">
          <div className="admin-card-header">
            <h2>All Users</h2>
          </div>
          <div className="admin-card-body">
            {loading ? (
              <div className="admin-loading">Loading...</div>
            ) : (
              <>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="admin-text-center admin-text-muted">No users found</td>
                        </tr>
                      ) : (
                        users.map(user => (
                          <tr
                            key={user.user_id}
                            className={selectedUser?.user_id === user.user_id ? 'selected' : ''}
                            onClick={() => handleSelectUser(user)}
                          >
                            <td>
                              <span className="user-name">{user.username || '-'}</span>
                            </td>
                            <td>
                              <span className="user-email">{user.email || '-'}</span>
                            </td>
                            <td>
                              <span className="user-balance">{user.credits_balance.toFixed(2)}</span>
                            </td>
                            <td>
                              <span className={`status-tag ${user.is_active ? 'status-tag--active' : 'status-tag--inactive'}`}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="admin-pagination">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span>Page {page} of {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 用户详情面板 */}
        {selectedUser && (
          <div className="admin-detail-panel">
            <div className="admin-detail-header">
              <h2>{selectedUser.username || selectedUser.email}</h2>
              <button className="admin-close-btn" onClick={() => setSelectedUser(null)}>×</button>
            </div>

            <div className="admin-detail-section">
              <h3 className="admin-detail-section-title">User Info</h3>
              <div className="admin-info-grid">
                <div className="admin-info-item">
                  <span className="admin-info-label">User ID</span>
                  <span className="admin-info-value admin-info-value--mono">{selectedUser.user_id}</span>
                </div>
                <div className="admin-info-item">
                  <span className="admin-info-label">Email</span>
                  <span className="admin-info-value">{selectedUser.email}</span>
                </div>
                <div className="admin-info-item">
                  <span className="admin-info-label">Balance</span>
                  <span className="admin-info-value admin-info-value--highlight">{selectedUser.credits_balance.toFixed(2)} credits</span>
                </div>
              </div>
            </div>

            {/* 充值表单 */}
            <div className="admin-detail-section">
              <h3 className="admin-detail-section-title">Grant Credits</h3>
              <div className="admin-form-row">
                <input
                  type="number"
                  placeholder="Amount"
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  className="admin-input"
                />
                <input
                  type="text"
                  placeholder="Reason"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="admin-input"
                />
              </div>
              <button
                className="admin-btn admin-btn--primary"
                onClick={handleGrant}
                disabled={granting || !grantAmount || !grantReason}
              >
                {granting ? 'Granting...' : 'Grant Credits'}
              </button>
            </div>

            {/* Credit Records */}
            <div className="admin-detail-section admin-detail-section--flex">
              <h3 className="admin-detail-section-title">Credit History</h3>
              {recordsLoading ? (
                <div className="admin-loading">Loading...</div>
              ) : (
                <>
                  <div className="admin-table-wrapper">
                    <table className="admin-table admin-table--compact">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Model</th>
                          <th>Tokens</th>
                          <th>Amount</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditRecords.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="admin-text-center admin-text-muted">No records</td>
                          </tr>
                        ) : (
                          creditRecords.map(record => (
                            <tr key={record.id}>
                              <td>{record.created_at ? new Date(record.created_at).toLocaleString() : '-'}</td>
                              <td>{record.biz_type_label || record.biz_type || '-'}</td>
                              <td>{record.model_name || '-'}</td>
                              <td>
                                {record.input_tokens || record.output_tokens
                                  ? `in:${record.input_tokens}/out:${record.output_tokens}`
                                  : record.total_tokens || '-'}
                              </td>
                              <td className={record.is_increase ? 'text-green' : 'text-red'}>
                                {record.is_increase ? '+' : '-'}{record.credit_cost.toFixed(2)}
                              </td>
                              <td>{record.after_balance.toFixed(2)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {recordsTotalPages > 1 && (
                    <div className="admin-pagination">
                      <button disabled={recordsPage === 1} onClick={() => setRecordsPage(p => p - 1)}>Prev</button>
                      <span>Page {recordsPage} of {recordsTotalPages}</span>
                      <button disabled={recordsPage === recordsTotalPages} onClick={() => setRecordsPage(p => p + 1)}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
